import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Api from "@ciq/shared/api/api"
import { useAppContext } from "@ciq/shared/hooks/context"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { BULK_API_LIMIT } from "@ciq/shared/pages/file_upload/constants"
import { buildMovePayload } from "@ciq/shared/pages/file_upload/helpers"

/**
 * @typedef {Object} UseFileListResult
 * @property {Set<string>} selectedIds - Set of selected file IDs
 * @property {boolean} allSelected - Boolean indicating if all visible files are selected
 * @property {string|null} statusFilter - Current status filter or null for all
 * @property {Array} filteredFiles - Files filtered by current status filter
 * @property {boolean} deleteConfirmOpen - Boolean indicating if delete confirmation modal is open
 * @property {Function} toggleSelect - Toggle selection of a file by ID
 * @property {Function} selectAll - Toggle select/deselect all visible files
 * @property {Function} setStatusFilter - Set the status filter value
 * @property {Function} updateStatus - Update a single file's status via API
 * @property {Function} bulkUpdateStatus - Bulk update selected files' status
 * @property {Function} requestBulkDelete - Open delete confirmation modal
 * @property {Function} confirmBulkDelete - Confirm and execute bulk delete
 * @property {Function} cancelBulkDelete - Cancel delete confirmation
 * @property {Function} download - Download a file by ID
 */

/**
 * Controller hook for file list management.
 * Manages selection, status filtering, API operations for status updates,
 * bulk operations, and file downloads.
 *
 * @param {Array} files - Array of uploaded file objects
 * @param {string} schoolId - Current school ID for API calls
 * @param {Function} onFilesChanged - Callback to trigger parent file re-fetch
 * @returns {UseFileListResult} Hook result with state and actions
 */
export function useFileList(files, schoolId, onFilesChanged) {
  const { addLoadingId, removeLoadingId } = useAppContext()
  const mountedRef = useRef(true)
  const controllersRef = useRef(new Set())
  useEffect(() => {
    const controllers = controllersRef.current
    return () => {
      mountedRef.current = false
      controllers.forEach(c => c.abort())
    }
  }, [])
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [statusFilter, setStatusFilter] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  // Clear selection when filter changes (prevents acting on non-visible files)
  useEffect(() => {
    setSelectedIds(new Set())
  }, [statusFilter])

  const filteredFiles = useMemo(() => {
    if (!statusFilter) return files
    return files.filter(f => f.placement_status === statusFilter)
  }, [files, statusFilter])

  // Reconcile selectedIds against visible files — removes IDs for deleted files
  // AND files that moved out of the active filter (e.g., status changed while filter active)
  useEffect(() => {
    if (!files || files.length === 0) return
    const visibleIds = new Set(filteredFiles.map(f => f.id))
    setSelectedIds(prev => {
      const reconciled = new Set([...prev].filter(id => visibleIds.has(id)))
      if (reconciled.size === prev.size) return prev
      return reconciled
    })
  }, [files, filteredFiles])

  const allSelected = useMemo(() => {
    return filteredFiles.length > 0 && filteredFiles.every(f => selectedIds.has(f.id))
  }, [filteredFiles, selectedIds])

  const toggleSelect = useCallback((fileId) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(fileId)) {
        next.delete(fileId)
      } else {
        next.add(fileId)
      }
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    if (selectedIds.size > 0) {
      setSelectedIds(new Set())
    } else {
      const ids = filteredFiles.map(f => f.id)
      if (ids.length > BULK_API_LIMIT) {
        setSelectedIds(new Set(ids.slice(0, BULK_API_LIMIT)))
      } else {
        setSelectedIds(new Set(ids))
      }
    }
  }, [selectedIds, filteredFiles])

  const updateStatus = useCallback(async (fileId, newStatus) => {
    const loadingId = "file_upload_status_update"
    addLoadingId(loadingId)
    const controller = new AbortController()
    controllersRef.current.add(controller)
    try {
      await Api.put(`schools/${schoolId}/uploaded-files/${fileId}`, {
        placement_status: newStatus,
      }, { signal: controller.signal })
      if (mountedRef.current) onFilesChanged()
    } catch (e) {
      if (e.handled) return
      if (mountedRef.current) ErrorToast("Unable to update file status")
    } finally {
      controllersRef.current.delete(controller)
      removeLoadingId(loadingId)
    }
  }, [schoolId, onFilesChanged, addLoadingId, removeLoadingId])

  const bulkUpdateStatus = useCallback(async (newStatus) => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return

    const loadingId = "file_upload_bulk_update"
    addLoadingId(loadingId)
    const controller = new AbortController()
    controllersRef.current.add(controller)
    try {
      await Api.put(`schools/${schoolId}/uploaded-files/bulk`, {
        files: ids.map(id => ({ id, placement_status: newStatus })),
      }, { signal: controller.signal })
      if (mountedRef.current) {
        setSelectedIds(new Set())
        onFilesChanged()
      }
    } catch (e) {
      if (e.handled) return
      if (mountedRef.current) ErrorToast("Unable to update file statuses")
    } finally {
      controllersRef.current.delete(controller)
      removeLoadingId(loadingId)
    }
  }, [schoolId, selectedIds, onFilesChanged, addLoadingId, removeLoadingId])

  const bulkMoveToBuilding = useCallback(async (targetBuildingId) => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return

    const loadingId = "file_upload_bulk_move"
    addLoadingId(loadingId)
    const controller = new AbortController()
    controllersRef.current.add(controller)
    try {
      const fileUpdates = ids.map(id => {
        const file = files.find(f => f.id === id)
        return file ? buildMovePayload(file, targetBuildingId) : { id, building_zone_id: targetBuildingId }
      })

      await Api.put(`schools/${schoolId}/uploaded-files/bulk`, {
        files: fileUpdates,
      }, { signal: controller.signal })
      if (mountedRef.current) {
        setSelectedIds(new Set())
        onFilesChanged()
      }
    } catch (e) {
      if (e.handled) return
      if (mountedRef.current) ErrorToast("Unable to move files")
    } finally {
      controllersRef.current.delete(controller)
      removeLoadingId(loadingId)
    }
  }, [schoolId, selectedIds, files, onFilesChanged, addLoadingId, removeLoadingId])

  const requestBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return
    setDeleteConfirmOpen(true)
  }, [selectedIds])

  const cancelBulkDelete = useCallback(() => {
    setDeleteConfirmOpen(false)
  }, [])

  const confirmBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return

    setDeleteConfirmOpen(false)
    const loadingId = "file_upload_bulk_delete"
    addLoadingId(loadingId)
    const controller = new AbortController()
    controllersRef.current.add(controller)
    try {
      await Api.delete(`schools/${schoolId}/uploaded-files/bulk`, {
        data: { file_ids: ids },
        signal: controller.signal,
      })
      if (mountedRef.current) {
        setSelectedIds(new Set())
        onFilesChanged()
      }
    } catch (e) {
      if (e.handled) return
      if (mountedRef.current) ErrorToast("Unable to delete files")
    } finally {
      controllersRef.current.delete(controller)
      removeLoadingId(loadingId)
    }
  }, [schoolId, selectedIds, onFilesChanged, addLoadingId, removeLoadingId])

  const download = useCallback(async (fileId) => {
    const loadingId = `file_upload_download_${fileId}`
    addLoadingId(loadingId)
    const controller = new AbortController()
    controllersRef.current.add(controller)
    try {
      const response = await Api.get(`schools/${schoolId}/uploaded-files/${fileId}/download-url?version=original`, { signal: controller.signal })
      const url = response.data.download_url
      if (!url) {
        ErrorToast("Unable to download file")
        return
      }
      window.open(url, "_blank")
    } catch (e) {
      if (e.handled) return
      console.error(e.error)
      ErrorToast("Unable to download file")
    } finally {
      controllersRef.current.delete(controller)
      removeLoadingId(loadingId)
    }
  }, [schoolId, addLoadingId, removeLoadingId])

  return {
    selectedIds,
    allSelected,
    statusFilter,
    filteredFiles,
    deleteConfirmOpen,
    toggleSelect,
    selectAll,
    setStatusFilter,
    updateStatus,
    bulkUpdateStatus,
    bulkMoveToBuilding,
    requestBulkDelete,
    confirmBulkDelete,
    cancelBulkDelete,
    download,
  }
}
